import React from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { Toaster } from './components/ui/sonner';
import { AuthProvider } from './lib/api';
import { ProtectedRoute, PublicRoute } from './components/ProtectedRoute';

// Auth Pages
import LoginPage from './pages/LoginPage';
import RegisterPage from './pages/RegisterPage';
import { ForgotPasswordPage, ResetPasswordPage } from './pages/PasswordPages';
import VerifyWarrantyPage from './pages/VerifyWarrantyPage';
import LandingPage from './pages/LandingPage';
import PrivacyPolicyPage from './pages/PrivacyPolicyPage';
import TermsPage from './pages/TermsPage';

// Admin Pages
import SuperAdminDashboard from './pages/admin/Dashboard';
import TenantsPage from './pages/admin/TenantsPage';
import WarrantyPlansPage from './pages/admin/WarrantyPlansPage';
import AdminInvoicesPage from './pages/admin/InvoicesPage';
import ProhibitedDevicesPage from './pages/admin/ProhibitedDevicesPage';
import InquiriesPage from './pages/admin/InquiriesPage';
import PhoneBrandsPage from './pages/admin/PhoneBrandsPage';
import CustomersPage from './pages/admin/CustomersPage';

// Shop Owner Pages
import ShopOwnerDashboard from './pages/shopowner/Dashboard';
import BranchesPage from './pages/shopowner/BranchesPage';
import StaffPage from './pages/shopowner/StaffPage';
import TenantProfilePage from './pages/shopowner/TenantProfilePage';

// Sales Staff Pages
import NewSalePage from './pages/sales/NewSalePage';
import BulkSalePage from './pages/sales/BulkSalePage';
import SalesHistoryPage from './pages/sales/SalesHistoryPage';
import MyPerformancePage from './pages/sales/MyPerformancePage';

// Shared Pages (will create simple placeholders)
import SalesListPage from './pages/shared/SalesListPage';
import CompliancePage from './pages/shared/CompliancePage';
import IncentivesPage from './pages/shared/IncentivesPage';
import ReportsPage from './pages/shared/ReportsPage';
import ShopOwnerInvoicesPage from './pages/shared/ShopOwnerInvoicesPage';
import WarrantyLookupPage from './pages/shared/WarrantyLookupPage';
import ClaimsPage from './pages/shared/ClaimsPage';
import StoreManagerDashboard from './pages/manager/Dashboard';
import ClaimPage from './pages/ClaimPage';

// Support Pages
import SupportDashboard from './pages/support/SupportDashboard';
import SupportManagementPage from './pages/admin/SupportManagementPage';
import ApprovalsPage from './pages/admin/ApprovalsPage';

import './App.css';
import { ChatWidget } from './components/ChatWidget';

function App() {
  return (
    <AuthProvider>
      <BrowserRouter>
        <Routes>
          {/* Public Routes */}
          <Route path="/" element={<LandingPage />} />
          <Route path="/privacy" element={<PrivacyPolicyPage />} />
          <Route path="/terms" element={<TermsPage />} />
          <Route path="/login" element={<PublicRoute><LoginPage /></PublicRoute>} />
          <Route path="/register" element={<PublicRoute><RegisterPage /></PublicRoute>} />
          <Route path="/forgot-password" element={<ForgotPasswordPage />} />
          <Route path="/reset-password" element={<ResetPasswordPage />} />
          <Route path="/verify" element={<VerifyWarrantyPage />} />
          <Route path="/claim" element={<ClaimPage />} />

          {/* Super Admin Routes */}
          <Route path="/admin" element={
            <ProtectedRoute allowedRoles={['SUPER_ADMIN']}>
              <SuperAdminDashboard />
            </ProtectedRoute>
          } />
          <Route path="/admin/tenants" element={
            <ProtectedRoute allowedRoles={['SUPER_ADMIN']}>
              <TenantsPage />
            </ProtectedRoute>
          } />
          <Route path="/admin/warranty-plans" element={
            <ProtectedRoute allowedRoles={['SUPER_ADMIN']}>
              <WarrantyPlansPage />
            </ProtectedRoute>
          } />
          <Route path="/admin/invoices" element={
            <ProtectedRoute allowedRoles={['SUPER_ADMIN']}>
              <AdminInvoicesPage />
            </ProtectedRoute>
          } />
          <Route path="/admin/prohibited-devices" element={
            <ProtectedRoute allowedRoles={['SUPER_ADMIN']}>
              <ProhibitedDevicesPage />
            </ProtectedRoute>
          } />
          <Route path="/admin/inquiries" element={
            <ProtectedRoute allowedRoles={['SUPER_ADMIN']}>
              <InquiriesPage />
            </ProtectedRoute>
          } />
          <Route path="/admin/phone-brands" element={
            <ProtectedRoute allowedRoles={['SUPER_ADMIN']}>
              <PhoneBrandsPage />
            </ProtectedRoute>
          } />
          <Route path="/admin/customers" element={
            <ProtectedRoute allowedRoles={['SUPER_ADMIN']}>
              <CustomersPage />
            </ProtectedRoute>
          } />
          <Route path="/admin/claims" element={
            <ProtectedRoute allowedRoles={['SUPER_ADMIN']}>
              <ClaimsPage />
            </ProtectedRoute>
          } />
          <Route path="/admin/compliance" element={
            <ProtectedRoute allowedRoles={['SUPER_ADMIN']}>
              <CompliancePage />
            </ProtectedRoute>
          } />
          <Route path="/admin/reports" element={
            <ProtectedRoute allowedRoles={['SUPER_ADMIN']}>
              <ReportsPage />
            </ProtectedRoute>
          } />
          <Route path="/admin/warranty-lookup" element={
            <ProtectedRoute allowedRoles={['SUPER_ADMIN']}>
              <WarrantyLookupPage />
            </ProtectedRoute>
          } />

          {/* Shop Owner Routes */}
          <Route path="/dashboard" element={
            <ProtectedRoute allowedRoles={['SHOP_OWNER']}>
              <ShopOwnerDashboard />
            </ProtectedRoute>
          } />
          <Route path="/dashboard/branches" element={
            <ProtectedRoute allowedRoles={['SHOP_OWNER']}>
              <BranchesPage />
            </ProtectedRoute>
          } />
          <Route path="/dashboard/staff" element={
            <ProtectedRoute allowedRoles={['SHOP_OWNER']}>
              <StaffPage />
            </ProtectedRoute>
          } />
          <Route path="/dashboard/sales" element={
            <ProtectedRoute allowedRoles={['SHOP_OWNER']}>
              <SalesListPage />
            </ProtectedRoute>
          } />
          <Route path="/dashboard/new-sale" element={
            <ProtectedRoute allowedRoles={['SHOP_OWNER']}>
              <NewSalePage />
            </ProtectedRoute>
          } />
          <Route path="/dashboard/invoices" element={
            <ProtectedRoute allowedRoles={['SHOP_OWNER']}>
              <ShopOwnerInvoicesPage />
            </ProtectedRoute>
          } />
          <Route path="/dashboard/incentives" element={
            <ProtectedRoute allowedRoles={['SHOP_OWNER']}>
              <IncentivesPage />
            </ProtectedRoute>
          } />
          <Route path="/dashboard/compliance" element={
            <ProtectedRoute allowedRoles={['SHOP_OWNER']}>
              <CompliancePage />
            </ProtectedRoute>
          } />
          <Route path="/dashboard/reports" element={
            <ProtectedRoute allowedRoles={['SHOP_OWNER']}>
              <ReportsPage />
            </ProtectedRoute>
          } />
          <Route path="/dashboard/warranty-lookup" element={
            <ProtectedRoute allowedRoles={['SHOP_OWNER']}>
              <WarrantyLookupPage />
            </ProtectedRoute>
          } />
          <Route path="/dashboard/claims" element={
            <ProtectedRoute allowedRoles={['SHOP_OWNER']}>
              <ClaimsPage />
            </ProtectedRoute>
          } />
          <Route path="/dashboard/profile" element={
            <ProtectedRoute allowedRoles={['SHOP_OWNER']}>
              <TenantProfilePage />
            </ProtectedRoute>
          } />

          {/* Store Manager Routes */}
          <Route path="/manager" element={
            <ProtectedRoute allowedRoles={['STORE_MANAGER']}>
              <StoreManagerDashboard />
            </ProtectedRoute>
          } />
          <Route path="/manager/sales" element={
            <ProtectedRoute allowedRoles={['STORE_MANAGER']}>
              <SalesListPage />
            </ProtectedRoute>
          } />
          <Route path="/manager/staff" element={
            <ProtectedRoute allowedRoles={['STORE_MANAGER']}>
              <IncentivesPage />
            </ProtectedRoute>
          } />
          <Route path="/manager/reports" element={
            <ProtectedRoute allowedRoles={['STORE_MANAGER']}>
              <ReportsPage />
            </ProtectedRoute>
          } />
          <Route path="/manager/new-sale" element={
            <ProtectedRoute allowedRoles={['STORE_MANAGER']}>
              <NewSalePage />
            </ProtectedRoute>
          } />
          <Route path="/manager/bulk" element={
            <ProtectedRoute allowedRoles={['STORE_MANAGER']}>
              <BulkSalePage />
            </ProtectedRoute>
          } />
          <Route path="/manager/incentives" element={
            <ProtectedRoute allowedRoles={['STORE_MANAGER']}>
              <IncentivesPage />
            </ProtectedRoute>
          } />
          <Route path="/manager/performance" element={
            <ProtectedRoute allowedRoles={['STORE_MANAGER']}>
              <MyPerformancePage />
            </ProtectedRoute>
          } />

          {/* Sales Staff Routes */}
          <Route path="/sales" element={
            <ProtectedRoute allowedRoles={['SALES_STAFF']}>
              <NewSalePage />
            </ProtectedRoute>
          } />
          <Route path="/sales/history" element={
            <ProtectedRoute allowedRoles={['SALES_STAFF']}>
              <SalesHistoryPage />
            </ProtectedRoute>
          } />
          <Route path="/sales/bulk" element={
            <ProtectedRoute allowedRoles={['SALES_STAFF']}>
              <BulkSalePage />
            </ProtectedRoute>
          } />
          <Route path="/sales/performance" element={
            <ProtectedRoute allowedRoles={['SALES_STAFF']}>
              <MyPerformancePage />
            </ProtectedRoute>
          } />
          <Route path="/sales/earnings" element={
            <ProtectedRoute allowedRoles={['SALES_STAFF']}>
              <IncentivesPage />
            </ProtectedRoute>
          } />

          {/* Support Agent Routes */}
          <Route path="/support" element={
            <ProtectedRoute allowedRoles={['SUPPORT_AGENT']}>
              <SupportDashboard />
            </ProtectedRoute>
          } />

          {/* Admin Support Management */}
          <Route path="/admin/support" element={
            <ProtectedRoute allowedRoles={['SUPER_ADMIN']}>
              <SupportManagementPage />
            </ProtectedRoute>
          } />
          <Route path="/admin/approvals" element={
            <ProtectedRoute allowedRoles={['SUPER_ADMIN']}>
              <ApprovalsPage />
            </ProtectedRoute>
          } />

          {/* Default Route */}
          <Route path="/" element={<Navigate to="/login" replace />} />
          <Route path="*" element={<Navigate to="/login" replace />} />
        </Routes>
      </BrowserRouter>
      <ChatWidget />
      <Toaster position="top-right" richColors />
    </AuthProvider>
  );
}

export default App;
